import {AddTestSegment} from "../../../../../../Server/Commands/TestSegmentCommands.js";
import {Session} from "../../../../../../Server/DB/sessions/@Session.js";
import {TestSegment} from "../../../../../../Server/DB/testSegments/@TestSegment.js";
import {MeID} from "../../../../../../Server/DB/users.js";
import {store} from "../../../../../../Store/index.js";
import {SetCurrentTime} from "../../../../../../Store/main/timeline.js";
import {RunInAction} from "web-vcore";
import {GetSelectedSessionExternals} from "../../../../../../Store/main/timeline/SessionDerivativeData.js";
import {Vec2} from "../../../../../../Utils/ThreeJS/VectorStructs.js";
import {styles} from "../../../../../../Utils/UI/GlobalStyles.js";
import React, {useEffect} from "react";
import {ShowSignInPopup} from "../../../../../@Shared/NavBar/UserPanel.js";
import {ShowMessageBox} from "react-vmessagebox";
import {ShowVMenu, VMenuItem} from "react-vmenu";
import {EEGSample} from "../../../../../Tools/@Shared/MuseInterface/EEGStructs.js";
import {SessionDataProcessor} from "../../../../../@Shared/Processors/SessionDataProcessor.js";
import moment from "moment";

export const useDetailChartUIEventListeners = (session: Session, processor: SessionDataProcessor, chartRef: React.RefObject<uPlot|null>)=>{
	// Chart setup effect
	useEffect(()=>{
		const chart = chartRef.current;
		if (chart == null) return;

		chart["session"] = session;

		const uplotDiv = chart.root as HTMLDivElement;
		const doubleClickStartDiv = uplotDiv.querySelector(".u-over") as HTMLDivElement;

		const handleDoubleClick = (e: MouseEvent)=>{
			e.stopPropagation();
		};

		const handleClick = (e: MouseEvent)=>{
			const clickedTime = chart.posToVal(e.offsetX, "x") * 1000;
			if (e.button == 0) {
				if (clickedTime.IsBetween(session.startTime, session.endTime)) {
					SetCurrentTime(clickedTime, session, {adjustViewRange: "none"});
				}
			}
		};

		const handleContextMenu = (e: MouseEvent)=>{
			const clickedTime = chart.posToVal(e.offsetX, "x") * 1000;
			const times = [
				store.main.timeline.sessions.currentTime,
				clickedTime,
			];
			const startTime_estimate = times.Min();
			const endTime_estimate = times.Max();
			const bufferStartTime_estimate = startTime_estimate - (store.main.settings.testSegmentStartBuffer * 1000);

			const menuUI = (
				<>
					<VMenuItem text="Create test-segment from yellow-line to here" style={styles.vMenuItem} onClick={()=>{
						if (MeID() == null) return ShowSignInPopup();
						const sessionExt = GetSelectedSessionExternals();
						if (sessionExt == null) return void ShowMessageBox({message: "Session data invalid or not fully loaded."});
						if ((sessionExt.eegSampleFiles.left?.samplesBySecond.VKeys().length ?? 0) == 0) return void ShowMessageBox({message: "Session data has no EEG samples."});

						const bufferStartSampleI = processor.eegProcessor.GetSampleIndexForTime(bufferStartTime_estimate, true);
						const endSampleI = processor.eegProcessor.GetSampleIndexForTime(endTime_estimate, true);
						if (bufferStartSampleI == null || endSampleI == null) {
							return void ShowMessageBox({message: "No EEG samples found in selected range."});
						}
						const samplesInSegment = [] as EEGSample[];
						for (let i = bufferStartSampleI; i < endSampleI; i++) {
							const sample: EEGSample = {
								time: processor.eegProcessor.samples_time[i] * 1000,
								left: processor.eegProcessor.samples_left[i],
								right: processor.eegProcessor.samples_right[i],
							};
							samplesInSegment.push(sample);
						}
						if (samplesInSegment.length == 0) return void ShowMessageBox({message: "No EEG samples found in selected range."});
						const bufferStartTime = samplesInSegment[0].time;
						const startTime = samplesInSegment.Last(a=>a.time < startTime_estimate).time;
						const endTime = samplesInSegment.Last().time;

						ShowMessageBox({
							title: "Create test-segment?",
							cancelButton: true,
							message: `
								Create a test-segment containing the ${samplesInSegment.length} EEG samples in the selected range?

								Start time: ${moment(startTime).format("HH:mm:ss.SSS")}
								End time: ${moment(endTime).format("HH:mm:ss.SSS")}
								Duration: ${((endTime - startTime) / 1000).RoundTo(.1)}s (please try to keep segments short, ie. <30s)
							`.AsMultiline(0),
							onOK: ()=>{
								if (processor.eegProcessor.samples_processingLevel[bufferStartSampleI] < 1) {
									processor.eegProcessor.ProcessSample_L1(bufferStartSampleI);
								}
								const firstBufferSample_cache = processor.eegProcessor.samples_cache[bufferStartSampleI];

								const newEntry = new TestSegment({
									enabled: true,
									sourceSession: session.id,
									bufferStartTime,
									startTime,
									endTime,
									channelBaselines: {
										eeg_left: firstBufferSample_cache.sumOverNormalizationWindow[0] / processor.eegProcessor.SamplesInNormalizationWindowFor(bufferStartSampleI),
										eeg_right: firstBufferSample_cache.sumOverNormalizationWindow[1] / processor.eegProcessor.SamplesInNormalizationWindowFor(bufferStartSampleI),
									},
									channelSamples: {
										eeg_left: samplesInSegment.map(a=>a.left),
										eeg_right: samplesInSegment.map(a=>a.right),
									},
									eyeMoveDeclarations: [],
								});
								new AddTestSegment({entry: newEntry}).RunOnServer();
							},
						});
					}}/>
				</>
			);
			ShowVMenu({
				pos: new Vec2(e.pageX, e.pageY),
			}, menuUI);
		};

		uplotDiv.addEventListener("dblclick", handleDoubleClick, {capture: true});
		doubleClickStartDiv.addEventListener("click", handleClick);
		doubleClickStartDiv.addEventListener("contextmenu", handleContextMenu);

		// hover: record the time under the cursor (used by the detail-panel "testing" tab to look up long-inhale debug-sample data)
		const handleMouseMove = (e: MouseEvent)=>{
			const hoveredTime = chart.posToVal(e.offsetX, "x") * 1000;
			RunInAction("DetailChart_Hover", ()=>{
				store.main.timeline.sessions.hoveredTime = hoveredTime;
			});
		};
		const handleMouseLeave = ()=>{
			RunInAction("DetailChart_HoverLeave", ()=>{
				store.main.timeline.sessions.hoveredTime = null;
			});
		};
		doubleClickStartDiv.addEventListener("mousemove", handleMouseMove);
		doubleClickStartDiv.addEventListener("mouseleave", handleMouseLeave);

		return ()=>{
			uplotDiv.removeEventListener("dblclick", handleDoubleClick, {capture: true});
			doubleClickStartDiv.removeEventListener("click", handleClick);
			doubleClickStartDiv.removeEventListener("contextmenu", handleContextMenu);
			doubleClickStartDiv.removeEventListener("mousemove", handleMouseMove);
			doubleClickStartDiv.removeEventListener("mouseleave", handleMouseLeave);
		};
	// we have to add chartRef.current to deps, so when chart is recreated, it gets the event-listeners
	// note: yes, long-term there is surely a cleaner solution than this
	// eslint-disable-next-line
	}, [session, processor, chartRef, chartRef.current]);
}